import { CDN_URL } from '../../config.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { pickStandingActionId } from '../../utils/spriteFallbacks.js';
import { showToast } from '../../utils/toast.js';

const PLACEHOLDER = '/assets/images/kiss.webp';

const normalizeGender = (gender) => (String(gender || '').toLowerCase() === 'male' ? 'male' : 'female');

const fetchCharacterThumb = async (item) => {
  const id = String(item?.id ?? '').trim();
  if (!id) return PLACEHOLDER;
  try {
    const gender = normalizeGender(item.gender);
    const data = await fetchCache.fetchCharacterSprite(id, pickStandingActionId(gender, id), 0, gender, false);
    return data?.spriteUrl || PLACEHOLDER;
  } catch {
    return PLACEHOLDER;
  }
};

const fetchScenarioThumb = (item) => {
  const id = String(item?.id ?? '').trim();
  if (!id) return PLACEHOLDER;
  return `${CDN_URL}/data/scenarios/images/${id}.webp`;
};

const isOwnedItemPrivate = (item) => {
  if (Boolean(item?.is_private)) return true;
  const status = Number(item?.status);
  return status === 1 || status === 11;
};

const sortOwnedEditItems = (items) => (
  [...items].sort((a, b) => {
    const aPrivate = isOwnedItemPrivate(a) ? 1 : 0;
    const bPrivate = isOwnedItemPrivate(b) ? 1 : 0;
    if (aPrivate !== bPrivate) return aPrivate - bPrivate;
    return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' });
  })
);

export const mountOwnedItemEditPicker = ({
  root,
  kind,
  titleFallback,
  validateFallback,
  fetchList,
  fetchItem,
  onValidate,
}) => {
  if (!root) return;

  const handleSelectCard = (card, cards) => {
    cards.forEach((el) => {
      el.classList.toggle('is-selected', el === card);
      el.setAttribute('aria-pressed', el === card ? 'true' : 'false');
    });
    root.dataset.selectedId = card.dataset.id || '';
  };

  const handleValidate = async () => {
    const selectedId = root.dataset.selectedId;
    if (!selectedId) {
      showToast(`Select a ${kind} to edit.`, 'exclamation-triangle');
      return;
    }
    const validateBtn = root.querySelector('.owned-edit-validate');
    if (validateBtn) validateBtn.disabled = true;
    try {
      const item = await fetchItem(selectedId);
      if (!item) throw new Error(`${kind} not found`);
      await onValidate(item);
    } catch (error) {
      console.error(`[owned-edit] failed to load ${kind}`, error);
      showToast(error.message || `Failed to load ${kind}`, 'exclamation-triangle');
    } finally {
      if (validateBtn) validateBtn.disabled = false;
    }
  };

  const render = async () => {
    let items = [];
    try {
      items = await fetchList();
    } catch (error) {
      console.warn(`[owned-edit] list failed for ${kind}`, error);
      root.classList.add('is-hidden');
      return;
    }
    if (!Array.isArray(items) || items.length === 0) {
      root.classList.add('is-hidden');
      return;
    }

    items = sortOwnedEditItems(items);

    root.classList.remove('is-hidden');
    const title = root.querySelector('.owned-edit-title');
    if (title && !title.textContent.trim()) title.textContent = titleFallback;
    const validateBtn = root.querySelector('.owned-edit-validate');
    if (validateBtn && !validateBtn.textContent.trim()) validateBtn.textContent = validateFallback;

    const grid = root.querySelector('.owned-edit-grid');
    if (!grid) return;
    grid.replaceChildren();

    const cards = [];
    items.forEach((item) => {
      const id = String(item.id ?? '');
      const name = String(item.name || `${kind} ${id}`);
      const isPrivate = isOwnedItemPrivate(item);
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'owned-edit-card';
      card.dataset.id = id;
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-pressed', 'false');
      card.setAttribute('aria-label', isPrivate ? `Edit ${name} (Private)` : `Edit ${name}`);
      const img = document.createElement('img');
      img.alt = name;
      img.loading = 'lazy';
      img.src = PLACEHOLDER;
      const label = document.createElement('span');
      label.className = 'owned-edit-card-name';
      label.textContent = name;
      if (isPrivate) {
        const badge = document.createElement('span');
        badge.className = 'picker-badge';
        badge.textContent = 'Private';
        card.append(img, badge, label);
      } else {
        card.append(img, label);
      }
      const handleActivate = () => handleSelectCard(card, cards);
      card.addEventListener('click', handleActivate);
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleActivate();
        }
      });
      grid.appendChild(card);
      cards.push(card);
      const thumbPromise = kind === 'character' ? fetchCharacterThumb(item) : Promise.resolve(fetchScenarioThumb(item));
      thumbPromise.then((url) => {
        img.src = url;
      });
      img.addEventListener('error', () => {
        img.src = PLACEHOLDER;
      });
    });

    validateBtn?.addEventListener('click', handleValidate);
  };

  render();
};
